import React, { useMemo } from "react";
import ComboboxAutocomplete from "./combobox_autocomplete";
import { BuildingOffice2Icon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const OPT_ALL = { id: "all", name: "Tutti" };
const OPT_NULL_OU = { id: "__NULL__", name: "Generici" };

export default function MyShiftsContextBar({
  units = [],
  selectedUnit,     // può essere: null (OU null), un oggetto OU, oppure "all"
  onSelectUnit,     // (ouObj) => void
  onResetUnit,      // "Tutti" (nessun filtro)

  isUnitsLoading = false,

  search = "",
  onSearchChange,

  placeholderUnit = "Seleziona Unità…",
  placeholderSearch = "Cerca turno per nome…",
  shiftTypeLabel = "",
}) {
  // label da mostrare
  const selectedUnitLabel =
    !selectedUnit || selectedUnit?.id === "__NULL__"
      ? "Generici"
      : selectedUnit?.id === "all"
      ? "Tutti"
      : (selectedUnit.name || selectedUnit.label || selectedUnit.descr || "");

  // value per la combobox
  const unitValue =
    selectedUnit?.id === "all"
      ? OPT_ALL
      : !selectedUnit || selectedUnit?.id === "__NULL__"
      ? OPT_NULL_OU
      : selectedUnit;

  // opzioni: Tutti + Generici + OU
  const options = useMemo(
    () => [OPT_ALL, OPT_NULL_OU, ...(units || [])],
    [units]
  );

  const handleReset = () => {
    onSelectUnit?.(OPT_ALL);   // Reset = Tutti
    onSearchChange?.("");
  };

  return (
    <section className="rounded-xl border border-indigo-100 bg-white/90 backdrop-blur shadow-sm ring-1 ring-black/5 w-full">
      {/* TOP */}
      <div className="px-4 py-3 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:flex-wrap">
          {/* Left label */}
          <div className="flex items-center gap-2 min-w-0 md:basis-72 md:flex-none">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
              <BuildingOffice2Icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-gray-600">Unità organizzativa</p>
              <div
                className={`mt-0.5 text-sm uppercase truncate ${
                  selectedUnit && selectedUnit?.id !== "all" ? "text-indigo-700 font-semibold" : "text-gray-700"
                }`}
              >
                {selectedUnitLabel}
              </div>
            </div>
          </div>

          {/* Center combobox */}
          <div className="min-w-0 md:flex-1 md:px-6 md:max-w-[50%]">
            {isUnitsLoading ? (
              <span className="inline-block h-9 w-full animate-pulse rounded-md bg-gray-100" />
            ) : (
              <div className="w-full border border-gray-200 rounded-lg shadow-sm">
                <ComboboxAutocomplete
                  options={options}
                  value={unitValue}
                  alwaysFirstIds={["all", "__NULL__"]}
                  onChange={(next) => {
                      // X della Combobox: ripristina "Tutti"
                      if (!next || next.id === "all") {
                          onSelectUnit?.(OPT_ALL);
                          return;
                      }

                      // Generici: turni senza unità organizzativa
                      if (next.id === "__NULL__") {
                          onSelectUnit?.(null);
                          return;
                      }

                      // Unità organizzativa specifica
                      onSelectUnit?.(next);
                  }}
                  placeholder={placeholderUnit}
                  bgActiveColor="bg-indigo-50"
                  textActiveColor="text-gray-700"
                  positionOptions="absolute"
                />
              </div>
            )}
          </div>

          {/* Reset */}
          <div className="flex w-full md:w-auto md:basis-48 md:flex-none justify-center md:justify-end">
            <button
              type="button"
              onClick={handleReset}
              disabled={(unitValue?.id === "all") && !search}
              className="
                shrink-0 whitespace-nowrap w-auto inline-flex items-center justify-center rounded-lg bg-indigo-600
                px-3 py-1.5 text-xs font-semibold text-white shadow-sm
                hover:bg-indigo-500 disabled:opacity-40
                focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500
              "
            >
              Reset filtri
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* SEARCH */}
      <div className="px-4 py-3 md:px-6">
        <label className="mb-1 block text-[11px] uppercase tracking-wide text-gray-600">
          Cerca turno
        </label>

        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          <input
            value={search}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={placeholderSearch}
            className="w-full bg-transparent text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none "
          />
          {search ? (
            <button
              type="button"
              onClick={() => onSearchChange?.("")}
              className="text-xs font-semibold text-indigo-700 hover:text-indigo-600"
            >
              Pulisci
            </button>
          ) : null}
        </div>

        <div className="mt-2 text-xs text-gray-500">
          La ricerca filtra per nome del turno
        </div>
      </div>

      {/* FOOTER */}
      <div className="rounded-b-2xl bg-gray-50 px-4 py-2 md:px-6">
        <div className="text-xs text-gray-500">
          {shiftTypeLabel ? (
            <>
              Tipo: <span className="font-semibold text-gray-700 normal-case capitalize" style={{ }}>{shiftTypeLabel}</span>
            </>
          ) : null}

            {" "}• Filtri attivi:{" "}
          <span className="font-semibold text-gray-700">{selectedUnitLabel}</span>

          {search ? (
            <>
              {" "}• Ricerca: <span className="font-semibold text-gray-700">{search}</span>
            </>
          ) : null}
        </div>
      </div>
    </section>
  );
}